/**
 * Invitation-code — operator UI (feat #172).
 *
 * Composition:
 *   - Gradient emerald->sky header.
 *   - Mint-code card with inviter id, code, scope, optional max_uses and
 *     expires_at.
 *   - Codes table with status pill, use-counts, expiry, and a "Redemptions"
 *     toggle that reveals a per-code redemptions drawer inline.
 *   - Inviter stats lookup card for on-demand attribution totals.
 *
 * Brand: emerald->sky headline, zinc borders, 6-8px rounded, light only,
 * no emojis, no competitor names.
 */
import {
	Fragment,
	useMemo,
	useState,
	type FormEvent,
} from "react";
import {
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import { toast } from "sonner";
import {
	createCode,
	deleteCode,
	formatDateOnly,
	formatUnixSec,
	formatUses,
	invitationCodeQueryKeys,
	inviterStats,
	listCodes,
	listRedemptions,
	normalizeCode,
	SCOPES,
	statusOf,
	updateCode,
	validateExpiresAt,
	validateInviterUserId,
	validateMaxUses,
	type InvitationCode,
	type InvitationScope,
	type InviterStats,
} from "./api";

// ---------------------------------------------------------------------------
// Style tokens.
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white shadow-[0_1px_3px_0_rgb(24_24_27/0.08),0_4px_8px_-2px_rgb(24_24_27/0.06)]";
const LABEL = "text-sm font-medium text-zinc-700";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20";
const SELECT = INPUT;
const BTN_PRIMARY =
	"rounded-[6px] border border-emerald-600 bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white shadow-[0_1px_2px_0_rgb(24_24_27/0.06)] hover:bg-emerald-700 hover:border-emerald-700 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_NEUTRAL =
	"rounded-[6px] border border-zinc-300 bg-white px-3 py-1.5 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_DANGER =
	"rounded-[6px] border border-rose-300 bg-white px-3 py-1.5 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:cursor-not-allowed disabled:opacity-60";
const HINT = "text-xs text-zinc-500";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export interface InvitationCodePageProps {
	initialInviterUserId?: string;
}

export function InvitationCodePage(props: InvitationCodePageProps = {}) {
	const queryClient = useQueryClient();

	const [filterInviter, setFilterInviter] = useState<string>(
		props.initialInviterUserId ?? "",
	);

	const appliedFilter = useMemo(() => {
		const v = validateInviterUserId(filterInviter);
		return v ?? undefined;
	}, [filterInviter]);

	const codesQuery = useQuery({
		queryKey: invitationCodeQueryKeys.codes(appliedFilter),
		queryFn: () => listCodes(appliedFilter),
	});

	const createMutation = useMutation({
		mutationFn: createCode,
		onSuccess: (code) => {
			toast.success(`Code "${code.code}" minted.`);
			queryClient.invalidateQueries({
				queryKey: invitationCodeQueryKeys.all,
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to create code.");
		},
	});

	const updateMutation = useMutation({
		mutationFn: updateCode,
		onSuccess: () => {
			toast.success("Code updated.");
			queryClient.invalidateQueries({
				queryKey: invitationCodeQueryKeys.all,
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to update code.");
		},
	});

	const deleteMutation = useMutation({
		mutationFn: deleteCode,
		onSuccess: () => {
			toast.success("Code removed.");
			queryClient.invalidateQueries({
				queryKey: invitationCodeQueryKeys.all,
			});
		},
		onError: (err: Error) => {
			toast.error(err.message || "Unable to delete code.");
		},
	});

	const [expandedCodeId, setExpandedCodeId] = useState<string | null>(null);

	const rows = codesQuery.data ?? [];

	return (
		<main
			data-testid="invitation-code-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10 sm:py-14"
		>
			<Header />

			<MintCodeCard
				onCreate={(args) => createMutation.mutate(args)}
				busy={createMutation.isPending}
			/>

			<CodesCard
				rows={rows}
				loading={codesQuery.isLoading}
				error={codesQuery.error?.message ?? null}
				filter={filterInviter}
				onFilterChange={setFilterInviter}
				expandedCodeId={expandedCodeId}
				onToggleExpand={(id) =>
					setExpandedCodeId((prev) => (prev === id ? null : id))
				}
				onToggleEnabled={(code) =>
					updateMutation.mutate({
						id: code.id,
						enabled: code.enabled !== 1,
					})
				}
				onDelete={(code) => {
					if (typeof window !== "undefined") {
						const ok = window.confirm(
							`Remove code "${code.code}" and all its redemptions?`,
						);
						if (!ok) return;
					}
					deleteMutation.mutate(code.id);
				}}
				busy={updateMutation.isPending || deleteMutation.isPending}
			/>

			<InviterStatsCard />
		</main>
	);
}

export default InvitationCodePage;

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function Header() {
	return (
		<header className="flex flex-col gap-2">
			<p className="text-xs font-semibold uppercase tracking-[0.12em] text-zinc-500">
				Tracking
			</p>
			<h1 className="bg-gradient-to-r from-emerald-600 to-sky-600 bg-clip-text text-3xl font-semibold tracking-tight text-transparent sm:text-4xl">
				Invitation codes
			</h1>
			<p className="max-w-prose text-sm text-zinc-600">
				Mint referral codes for user-invites-user flows. Redemptions attribute
				back to the inviter, cap at an optional max-uses, and expire on a
				schedule. Double redemptions by the same invitee never double-count.
			</p>
		</header>
	);
}

function MintCodeCard(props: {
	onCreate: (args: {
		inviter_user_id: string;
		scope: InvitationScope;
		code: string;
		max_uses?: number;
		expires_at?: number | null;
	}) => void;
	busy: boolean;
}) {
	const [inviter, setInviter] = useState("");
	const [code, setCode] = useState("");
	const [scope, setScope] = useState<InvitationScope>("affiliate");
	const [maxUses, setMaxUses] = useState<string>("0");
	const [expiresAt, setExpiresAt] = useState<string>("");
	const [errors, setErrors] = useState<{
		inviter?: string;
		code?: string;
		maxUses?: string;
		expiresAt?: string;
	}>({});

	const disabled = props.busy;

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const next: typeof errors = {};

		const inviterOk = validateInviterUserId(inviter);
		if (!inviterOk) next.inviter = "Enter a valid inviter user id.";

		const codeOk = normalizeCode(code);
		if (!codeOk) next.code = "Code must be 3-64 chars (letters, digits, . _ -).";

		const maxUsesOk = validateMaxUses(maxUses);
		if (maxUsesOk === null) next.maxUses = "Enter 0 for unlimited, or a non-negative integer.";

		const expiresAtOk = validateExpiresAt(expiresAt);
		if (expiresAtOk === "invalid") next.expiresAt = "Pick a valid future date/time.";

		setErrors(next);
		if (Object.keys(next).length > 0) return;

		props.onCreate({
			inviter_user_id: inviterOk!,
			scope,
			code: codeOk!,
			max_uses: maxUsesOk ?? 0,
			expires_at: expiresAtOk == null || expiresAtOk === "invalid" ? null : expiresAtOk,
		});

		// Reset just the code so operators can batch-mint.
		setCode("");
	};

	return (
		<section className={`${CARD} p-5`} aria-label="Mint invitation code">
			<h2 className="text-base font-semibold text-zinc-900">
				Mint a new code
			</h2>
			<p className={HINT}>
				Codes are normalised to upper-case. Max uses of 0 means unlimited.
			</p>

			<form
				onSubmit={handleSubmit}
				className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2"
				data-testid="mint-code-form"
			>
				<div className="flex flex-col gap-1.5">
					<label htmlFor="inviter-user-id" className={LABEL}>
						Inviter user id
					</label>
					<input
						id="inviter-user-id"
						name="inviter_user_id"
						type="text"
						required
						maxLength={128}
						className={INPUT}
						value={inviter}
						onChange={(e) => setInviter(e.currentTarget.value)}
						disabled={disabled}
						data-testid="mint-inviter-input"
					/>
					{errors.inviter ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.inviter}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="code-value" className={LABEL}>
						Code
					</label>
					<input
						id="code-value"
						name="code"
						type="text"
						required
						maxLength={64}
						className={INPUT}
						value={code}
						onChange={(e) => setCode(e.currentTarget.value)}
						disabled={disabled}
						data-testid="mint-code-input"
					/>
					{errors.code ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.code}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="scope" className={LABEL}>
						Scope
					</label>
					<select
						id="scope"
						name="scope"
						className={SELECT}
						value={scope}
						onChange={(e) =>
							setScope(e.currentTarget.value as InvitationScope)
						}
						disabled={disabled}
						data-testid="mint-scope-select"
					>
						{SCOPES.map((s) => (
							<option key={s} value={s}>
								{s}
							</option>
						))}
					</select>
				</div>

				<div className="flex flex-col gap-1.5">
					<label htmlFor="max-uses" className={LABEL}>
						Max uses
					</label>
					<input
						id="max-uses"
						name="max_uses"
						type="number"
						min={0}
						max={1000000}
						className={INPUT}
						value={maxUses}
						onChange={(e) => setMaxUses(e.currentTarget.value)}
						disabled={disabled}
						data-testid="mint-max-uses-input"
					/>
					{errors.maxUses ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.maxUses}
						</p>
					) : null}
				</div>

				<div className="flex flex-col gap-1.5 sm:col-span-2">
					<label htmlFor="expires-at" className={LABEL}>
						Expires at (optional)
					</label>
					<input
						id="expires-at"
						name="expires_at"
						type="datetime-local"
						className={INPUT}
						value={expiresAt}
						onChange={(e) => setExpiresAt(e.currentTarget.value)}
						disabled={disabled}
						data-testid="mint-expires-input"
					/>
					{errors.expiresAt ? (
						<p className="text-xs text-rose-600" role="alert">
							{errors.expiresAt}
						</p>
					) : null}
				</div>

				<div className="sm:col-span-2">
					<button
						type="submit"
						className={BTN_PRIMARY}
						disabled={disabled}
						data-testid="mint-code-submit"
					>
						{props.busy ? "Minting." : "Mint code"}
					</button>
				</div>
			</form>
		</section>
	);
}

function CodesCard(props: {
	rows: readonly InvitationCode[];
	loading: boolean;
	error: string | null;
	filter: string;
	onFilterChange: (v: string) => void;
	expandedCodeId: string | null;
	onToggleExpand: (id: string) => void;
	onToggleEnabled: (code: InvitationCode) => void;
	onDelete: (code: InvitationCode) => void;
	busy: boolean;
}) {
	return (
		<section className={`${CARD} flex flex-col`} aria-label="Invitation codes">
			<header className="flex flex-col gap-3 border-b border-zinc-200 p-5 sm:flex-row sm:items-end sm:justify-between">
				<div className="flex flex-col gap-1">
					<h2 className="text-base font-semibold text-zinc-900">
						Codes ({props.rows.length})
					</h2>
					<p className={HINT}>
						{props.rows.length === 0
							? "No codes minted yet."
							: "Toggle enabled, inspect redemptions, or remove codes as needed."}
					</p>
				</div>

				<div className="flex items-end gap-2">
					<div className="flex flex-col gap-1.5">
						<label htmlFor="filter-inviter" className={LABEL}>
							Filter by inviter
						</label>
						<input
							id="filter-inviter"
							type="text"
							className={`${INPUT} sm:w-64`}
							value={props.filter}
							onChange={(e) => props.onFilterChange(e.currentTarget.value)}
							placeholder="(all inviters)"
							data-testid="filter-inviter-input"
						/>
					</div>
				</div>
			</header>

			{props.error ? (
				<div className="p-5 text-sm text-rose-600" role="alert">
					{props.error}
				</div>
			) : props.loading ? (
				<div className="p-5 text-sm text-zinc-500">Loading codes.</div>
			) : props.rows.length === 0 ? (
				<div className="flex flex-col items-center gap-1 p-10 text-center">
					<p className="text-sm font-medium text-zinc-700">
						No invitation codes yet.
					</p>
					<p className="text-xs text-zinc-500">
						Mint a code above to start tracking referrals.
					</p>
				</div>
			) : (
				<CodesTable
					rows={props.rows}
					expandedCodeId={props.expandedCodeId}
					onToggleExpand={props.onToggleExpand}
					onToggleEnabled={props.onToggleEnabled}
					onDelete={props.onDelete}
					busy={props.busy}
				/>
			)}
		</section>
	);
}

function CodesTable(props: {
	rows: readonly InvitationCode[];
	expandedCodeId: string | null;
	onToggleExpand: (id: string) => void;
	onToggleEnabled: (code: InvitationCode) => void;
	onDelete: (code: InvitationCode) => void;
	busy: boolean;
}) {
	return (
		<div className="overflow-x-auto">
			<table
				className="w-full border-collapse text-left text-sm"
				data-testid="codes-table"
			>
				<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wide text-zinc-500">
					<tr>
						<Th>Code</Th>
						<Th>Inviter</Th>
						<Th>Scope</Th>
						<Th>Status</Th>
						<Th>Uses</Th>
						<Th>Expires</Th>
						<Th>Created</Th>
						<Th className="text-right">Actions</Th>
					</tr>
				</thead>
				<tbody className="divide-y divide-zinc-200">
					{props.rows.map((row) => {
						const expanded = props.expandedCodeId === row.id;
						return (
							<Fragment key={row.id}>
								<tr
									data-testid={`code-row-${row.id}`}
									className="hover:bg-zinc-50/80"
								>
									<Td>
										<span className="font-mono font-medium text-zinc-900">
											{row.code}
										</span>
									</Td>
									<Td>
										<span className="font-mono text-xs text-zinc-700">
											{row.inviter_user_id}
										</span>
									</Td>
									<Td>
										<ScopeBadge scope={row.scope} />
									</Td>
									<Td>
										<StatusPill code={row} />
									</Td>
									<Td>
										<span className="font-mono text-xs text-zinc-700">
											{formatUses(row)}
										</span>
									</Td>
									<Td>
										<span className="text-zinc-600">
											{formatUnixSec(row.expires_at)}
										</span>
									</Td>
									<Td>
										<span className="text-zinc-600">
											{formatDateOnly(row.created_at)}
										</span>
									</Td>
									<Td className="text-right">
										<div className="inline-flex flex-wrap justify-end gap-1.5">
											<button
												type="button"
												onClick={() => props.onToggleExpand(row.id)}
												className={BTN_NEUTRAL}
												data-testid={`toggle-redemptions-${row.id}`}
											>
												{expanded ? "Hide" : "Redemptions"}
											</button>
											<button
												type="button"
												onClick={() => props.onToggleEnabled(row)}
												className={BTN_NEUTRAL}
												disabled={props.busy}
												data-testid={`toggle-enabled-${row.id}`}
											>
												{row.enabled === 1 ? "Disable" : "Enable"}
											</button>
											<button
												type="button"
												onClick={() => props.onDelete(row)}
												className={BTN_DANGER}
												disabled={props.busy}
												data-testid={`delete-${row.id}`}
											>
												Remove
											</button>
										</div>
									</Td>
								</tr>
								{expanded ? (
									<tr>
										<td colSpan={8} className="bg-zinc-50/60 p-0">
											<RedemptionsDrawer codeId={row.id} />
										</td>
									</tr>
								) : null}
							</Fragment>
						);
					})}
				</tbody>
			</table>
		</div>
	);
}

function RedemptionsDrawer(props: { codeId: string }) {
	const q = useQuery({
		queryKey: invitationCodeQueryKeys.redemptions(props.codeId),
		queryFn: () => listRedemptions(props.codeId, 100),
	});

	const rows = q.data ?? [];

	return (
		<div className="p-5" data-testid={`redemptions-drawer-${props.codeId}`}>
			<h3 className="text-sm font-semibold text-zinc-900">Redemptions</h3>
			{q.isLoading ? (
				<p className="mt-2 text-xs text-zinc-500">Loading redemptions.</p>
			) : q.error ? (
				<p className="mt-2 text-xs text-rose-600" role="alert">
					{q.error.message}
				</p>
			) : rows.length === 0 ? (
				<p className="mt-2 text-xs text-zinc-500">No redemptions yet.</p>
			) : (
				<ul
					className="mt-3 divide-y divide-zinc-200 rounded-[6px] border border-zinc-200 bg-white"
					data-testid="redemptions-list"
				>
					{rows.map((r) => (
						<li
							key={r.id}
							className="flex flex-col gap-0.5 px-3 py-2 text-xs text-zinc-700 sm:flex-row sm:items-center sm:justify-between"
						>
							<span className="font-mono">{r.invitee_identifier}</span>
							<span className="text-zinc-500">
								{formatUnixSec(r.redeemed_at)}
								{r.ip ? ` · ${r.ip}` : ""}
							</span>
						</li>
					))}
				</ul>
			)}
		</div>
	);
}

function ScopeBadge(props: { scope: InvitationScope }) {
	const tones: Record<InvitationScope, string> = {
		affiliate: "border-emerald-200 bg-emerald-50 text-emerald-700",
		advertiser: "border-sky-200 bg-sky-50 text-sky-700",
		admin: "border-zinc-300 bg-zinc-100 text-zinc-700",
	};
	return (
		<span
			className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium ${tones[props.scope]}`}
		>
			{props.scope}
		</span>
	);
}

function StatusPill(props: { code: InvitationCode }) {
	const s = statusOf(props.code);
	const tones: Record<ReturnType<typeof statusOf>, string> = {
		active: "border-emerald-200 bg-emerald-50 text-emerald-700",
		disabled: "border-zinc-300 bg-zinc-50 text-zinc-600",
		expired: "border-amber-200 bg-amber-50 text-amber-700",
		capped: "border-sky-200 bg-sky-50 text-sky-700",
	};
	return (
		<span
			className={`inline-flex items-center rounded-[6px] border px-2 py-0.5 text-xs font-medium ${tones[s]}`}
			data-testid={`status-pill-${props.code.id}`}
		>
			{s}
		</span>
	);
}

function InviterStatsCard() {
	const [inviter, setInviter] = useState("");
	const [applied, setApplied] = useState<string | null>(null);
	const [fieldError, setFieldError] = useState<string | null>(null);

	const q = useQuery<InviterStats>({
		queryKey: applied
			? invitationCodeQueryKeys.stats(applied)
			: ["invitation-code", "stats", "idle"],
		queryFn: () => inviterStats(applied as string),
		enabled: !!applied,
	});

	const handleApply = (e: FormEvent) => {
		e.preventDefault();
		const ok = validateInviterUserId(inviter);
		if (!ok) {
			setFieldError("Enter a valid inviter user id.");
			setApplied(null);
			return;
		}
		setFieldError(null);
		setApplied(ok);
	};

	const stats = q.data ?? null;

	return (
		<section className={`${CARD} flex flex-col`} aria-label="Inviter stats">
			<header className="flex flex-col gap-1 border-b border-zinc-200 p-5">
				<h2 className="text-base font-semibold text-zinc-900">
					Inviter stats
				</h2>
				<p className={HINT}>
					Look up totals for a single inviter user id.
				</p>
			</header>

			<form
				onSubmit={handleApply}
				className="flex flex-col gap-3 p-5 sm:flex-row sm:items-end"
				data-testid="stats-form"
			>
				<div className="flex w-full flex-col gap-1.5 sm:max-w-sm">
					<label htmlFor="stats-inviter" className={LABEL}>
						Inviter user id
					</label>
					<input
						id="stats-inviter"
						type="text"
						className={INPUT}
						value={inviter}
						onChange={(e) => setInviter(e.currentTarget.value)}
						data-testid="stats-inviter-input"
					/>
					{fieldError ? (
						<p className="text-xs text-rose-600" role="alert">
							{fieldError}
						</p>
					) : null}
				</div>
				<button
					type="submit"
					className={BTN_PRIMARY}
					data-testid="stats-apply"
				>
					Look up
				</button>
			</form>

			{applied ? (
				q.isLoading ? (
					<div className="p-5 text-sm text-zinc-500">Loading stats.</div>
				) : q.error ? (
					<div className="p-5 text-sm text-rose-600" role="alert">
						{q.error.message}
					</div>
				) : stats ? (
					<StatsTable stats={stats} />
				) : null
			) : null}
		</section>
	);
}

function StatsTable(props: { stats: InviterStats }) {
	const { stats } = props;
	return (
		<div className="flex flex-col gap-4 p-5">
			<div className="grid grid-cols-2 gap-3 sm:max-w-md">
				<SummaryBox label="Codes" value={stats.totals.codes} />
				<SummaryBox label="Redemptions" value={stats.totals.redemptions} />
			</div>
			{stats.per_code.length > 0 ? (
				<div className="overflow-x-auto">
					<table
						className="w-full border-collapse text-left text-sm"
						data-testid="stats-per-code-table"
					>
						<thead className="bg-zinc-50 text-xs font-medium uppercase tracking-wide text-zinc-500">
							<tr>
								<Th>Code</Th>
								<Th>Scope</Th>
								<Th>Enabled</Th>
								<Th>Use count</Th>
								<Th>Max uses</Th>
								<Th>Redemptions</Th>
							</tr>
						</thead>
						<tbody className="divide-y divide-zinc-200">
							{stats.per_code.map((r) => (
								<tr key={r.code_id} data-testid={`stats-row-${r.code_id}`}>
									<Td>
										<span className="font-mono text-zinc-900">{r.code}</span>
									</Td>
									<Td>
										<ScopeBadge scope={r.scope} />
									</Td>
									<Td>
										<span className="text-zinc-700">
											{r.enabled === 1 ? "Yes" : "No"}
										</span>
									</Td>
									<Td>
										<span className="font-mono text-xs text-zinc-700">
											{r.use_count}
										</span>
									</Td>
									<Td>
										<span className="font-mono text-xs text-zinc-700">
											{r.max_uses === 0 ? "∞" : r.max_uses}
										</span>
									</Td>
									<Td>
										<span className="font-mono text-xs text-zinc-900">
											{r.redemptions}
										</span>
									</Td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			) : (
				<p className={HINT}>No codes on record for this inviter.</p>
			)}
		</div>
	);
}

function SummaryBox(props: { label: string; value: number }) {
	return (
		<div
			className="rounded-[6px] border border-zinc-200 bg-zinc-50/60 px-4 py-3"
			data-testid={`summary-${props.label.toLowerCase()}`}
		>
			<p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">
				{props.label}
			</p>
			<p className="mt-1 text-xl font-semibold tabular-nums text-zinc-900">
				{props.value}
			</p>
		</div>
	);
}

function Th(props: { children: React.ReactNode; className?: string }) {
	return (
		<th scope="col" className={`px-4 py-2.5 ${props.className ?? ""}`.trim()}>
			{props.children}
		</th>
	);
}

function Td(props: { children: React.ReactNode; className?: string }) {
	return (
		<td
			className={`px-4 py-3 align-top text-zinc-700 ${props.className ?? ""}`.trim()}
		>
			{props.children}
		</td>
	);
}

// Named-alias export to match the convention in sibling features.
export { InvitationCodePage as InvitationCodePageNamed };
